"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Building2, House as Home, ClipboardCheck, Award, ArrowRight, CircleCheck as CheckCircle2, HardHat, Eye as EyeCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const accessContexts = [{
  title: "Period and heritage residences",
  description: "Careful ties, padded protective uprights, and tailored layouts designed around older brickwork, timber framing, and decorative stonework.",
  icon: Home,
  tag: "Residential"
}, {
  title: "Affluent residential roads and private drives",
  description: "Tidy footprints, ground protection for paved surfaces, and considerate scheduling that respects neighbors and private access.",
  icon: HardHat,
  tag: "Care & Discretion"
}, {
  title: "Occupied commercial premises",
  description: "Debris netting, fan protection, and clear pedestrian walkways to keep customers and staff moving safely beneath active works.",
  icon: Building2,
  tag: "Commercial"
}];
const safetySteps = [{
  title: "Initial site assessment",
  detail: "We inspect ground conditions, building fabric, overhead obstacles, and property access before a single tube arrives."
}, {
  title: "Safe access planning",
  detail: "Erection schedules engineered to minimise disruption, ensuring emergency routes and normal property access stay open."
}, {
  title: "Handover inspection certificate",
  detail: "Every structure is formally signed off and tagged prior to your contractors setting foot on the boards."
}];
const accreditationBadges = [{
  name: "NASC member",
  detail: "National Access & Scaffolding Confederation compliant practices and trained operatives."
}, {
  name: "CHAS accredited",
  detail: "Independent health and safety pre-qualification standards verified annually."
}];
export default function BeaconsfieldAccess() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  return <section id="beaconsfieldsafetyandaccess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-normal text-xs uppercase tracking-wide">
              Beaconsfield Access & Safety
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Scaffolding built for Beaconsfield homes and occupied premises.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            From listed heritage residences to active town businesses, we plan safe access around delicate masonry, private driveways, and daily foot traffic.
          </p>
        </div>

        {/* Local Access Contexts */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {accessContexts.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} variants={fadeUp} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="p-2.5 rounded-lg bg-accent text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs font-normal">
                        {item.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Safety Protocol & Accreditations Block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bg-muted/40 rounded-2xl p-6 sm:p-8 md:p-10 border border-border">
          {/* Left: Safety Workflow */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary font-medium text-sm mb-2">
                <ShieldCheck className="size-4" />
                <span>Standard safety procedures</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-foreground mb-4">
                Structured inspection and handover on every project.
              </h3>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                We operate to rigorous national standards, ensuring your builders, roofers, and decorators have a secure platform without compromising the surrounding property.
              </p>

              <div className="space-y-4">
                {safetySteps.map((step, i) => <div key={step.title} data-index={i} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card border border-border/80">
                    <div className="mt-0.5 shrink-0 text-primary">
                      <CheckCircle2 className="size-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {step.title}
                      </p>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">
                        {step.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="pt-6 mt-6 border-t border-border flex flex-wrap items-center gap-4">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a site visit</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-border">
                <Link to="/HealthSafetyAccreditationsPage">
                  <span>View safety credentials</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right: Accreditations Card */}
          <div className="lg:col-span-5 flex flex-col justify-between rounded-xl bg-card p-6 sm:p-7 border border-border shadow-xs">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Award className="size-5 text-primary" />
                <h4 className="font-semibold text-base text-foreground">
                  Recognised safety accreditations
                </h4>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
                Our safety framework is backed by recognised industry bodies. We undergo regular audits to confirm compliance with national safety directives.
              </p>

              <div className="space-y-4 mb-6">
                {accreditationBadges.map((acc, i) => <div key={acc.name} data-index={i} className="p-4 rounded-lg bg-muted/30 border border-border">
                    <div className="flex items-center gap-2 mb-1">
                      <ClipboardCheck className="size-4 text-primary shrink-0" />
                      <span className="font-semibold text-sm text-foreground">
                        {acc.name}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pl-6">
                      {acc.detail}
                    </p>
                  </div>)}
              </div>
            </div>

            <div className="bg-accent/40 rounded-lg p-4 border border-border text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5 font-medium text-foreground mb-1">
                <EyeCheck className="size-3.5 text-primary" />
                <span>Regular 7-day statutory inspections</span>
              </div>
              Weekly scaffold inspections and signed register updates carried out throughout the project duration.
            </div>
          </div>
        </div>
      </div>
    </section>;
}